import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format, differenceInHours, differenceInDays, addDays, isPast } from "date-fns";
import { Megaphone, MessageSquare, Plus, Send, X, Image as ImageIcon, Edit3, Trash2, ChevronDown, ChevronUp, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, type Announcement } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/cn";
import { Avatar } from "../components/ui/Avatar";
import { SpotlightCard } from "../components/ui/SpotlightCard";

const announcementSchema = z.object({
  title: z.string().min(3, "Title must be at least 3 characters").max(255),
  content: z.string().min(5, "Content must be at least 5 characters").max(10000),
});

const commentSchema = z.object({
  content: z.string().min(1, "Comment cannot be empty").max(10000),
});

type AnnouncementFormValues = z.infer<typeof announcementSchema>;
type CommentFormValues = z.infer<typeof commentSchema>;

const CONTENT_CHAR_LIMIT = 300;
const EXPIRY_DAYS = 3;

function getExpiryStatus(createdAt: string) {
  const expiresAt = addDays(new Date(createdAt), EXPIRY_DAYS);
  const now = new Date();

  if (isPast(expiresAt)) {
    return { label: "Expired", variant: "expired" as const };
  }

  const hoursLeft = differenceInHours(expiresAt, now);
  const daysLeft = differenceInDays(expiresAt, now);

  if (hoursLeft <= 12) {
    return { label: `Expires in ${hoursLeft}h`, variant: "critical" as const };
  }
  if (daysLeft <= 1) {
    return { label: `Expires in ${hoursLeft}h`, variant: "warning" as const };
  }
  return { label: `${daysLeft}d left`, variant: "ok" as const };
}

export default function Announcements() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["announcements"],
    queryFn: async () => {
      const res = await api.get<{ items: Announcement[] }>("/api/announcements");
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await api.post<{ announcement: Announcement }>("/api/announcements", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data.announcement;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
      toast.success("Announcement posted!");
      setIsCreating(false);
      createForm.reset();
      setImageFile(null);
      setImagePreview(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || "Failed to post announcement");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, formData }: { id: string; formData: FormData }) => {
      const res = await api.post<{ announcement: Announcement }>(`/api/announcements/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data.announcement;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
      queryClient.setQueryData(["announcements", data.id], data);
      toast.success("Announcement updated!");
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || "Failed to update announcement");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/announcements/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
      toast.success("Announcement deleted!");
      setExpandedId(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || "Failed to delete announcement");
    },
  });

  const commentMutation = useMutation({
    mutationFn: async ({ id, content }: { id: string; content: string }) => {
      const res = await api.post(`/api/announcements/${id}/comments`, { content });
      return res.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["announcements", id] });
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
      toast.success("Comment added!");
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || "Failed to add comment");
    },
  });

  const createForm = useForm<AnnouncementFormValues>({
    resolver: zodResolver(announcementSchema),
    defaultValues: { title: "", content: "" },
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const onPostAnnouncement = (values: AnnouncementFormValues) => {
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("content", values.content);
    if (imageFile) {
      formData.append("image", imageFile);
    }
    createMutation.mutate(formData);
  };

  const canPost = user?.role === "IT";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-brand-400" />
            Announcements
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Stay updated with the latest news and information.
          </p>
        </div>
        {canPost && !isCreating && (
          <button
            onClick={() => setIsCreating(true)}
            className="btn-primary"
          >
            <Plus className="w-4 h-4" />
            Post Announcement
          </button>
        )}
      </div>

      {isCreating && canPost && (
        <SpotlightCard className="p-6 animate-in slide-in-from-top-4 fade-in">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-white">New Announcement</h2>
            <button
              onClick={() => setIsCreating(false)}
              className="text-slate-400 hover:text-white transition-colors p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={createForm.handleSubmit(onPostAnnouncement)} className="space-y-4">
            <div>
              <label className="label">Title</label>
              <input
                {...createForm.register("title")}
                className="input"
                placeholder="Announcement Title"
              />
              {createForm.formState.errors.title && (
                <p className="field-error">{createForm.formState.errors.title.message}</p>
              )}
            </div>
            <div>
              <label className="label">Content</label>
              <textarea
                {...createForm.register("content")}
                rows={4}
                className="textarea"
                placeholder="What's the news?"
              />
              {createForm.formState.errors.content && (
                <p className="field-error">{createForm.formState.errors.content.message}</p>
              )}
            </div>
            <div>
              <label className="label">Image (Optional)</label>
              <div className="flex items-center gap-4">
                <label className="btn-secondary cursor-pointer shrink-0">
                  <ImageIcon className="w-4 h-4" />
                  {imageFile ? "Change Image" : "Upload Image"}
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                </label>
                {imagePreview && (
                  <div className="relative w-16 h-16 rounded-xl overflow-hidden border border-white/10 shrink-0">
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setImageFile(null);
                        setImagePreview(null);
                      }}
                      className="absolute top-1 right-1 p-0.5 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="btn-primary px-6"
              >
                {createMutation.isPending ? "Posting..." : "Post Announcement"}
              </button>
            </div>
          </form>
        </SpotlightCard>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data?.items?.length === 0 ? (
        <div className="text-center py-12 bg-white/5 border border-white/10 rounded-2xl">
          <Megaphone className="w-12 h-12 text-slate-500 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-white mb-1">No announcements yet</h3>
          <p className="text-slate-400">Check back later for updates.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {data?.items?.map((announcement) => (
            <AnnouncementCard
              key={announcement.id}
              announcement={announcement}
              isExpanded={expandedId === announcement.id}
              onToggle={() => setExpandedId(expandedId === announcement.id ? null : announcement.id)}
              onComment={(content) => commentMutation.mutate({ id: announcement.id, content })}
              isCommenting={commentMutation.isPending}
              onUpdate={async (id, formData) => { await updateMutation.mutateAsync({ id, formData }); }}
              onDelete={(id) => deleteMutation.mutate(id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AnnouncementCard({
  announcement,
  isExpanded,
  onToggle,
  onComment,
  isCommenting,
  onUpdate,
  onDelete,
}: {
  announcement: Announcement;
  isExpanded: boolean;
  onToggle: () => void;
  onComment: (content: string) => void;
  isCommenting: boolean;
  onUpdate?: (id: string, formData: FormData) => Promise<void>;
  onDelete?: (id: string) => void;
}) {
  const { user } = useAuth();
  const canEdit = user?.role === "IT";
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showFullContent, setShowFullContent] = useState(false);

  const isLongContent = announcement.content.length > CONTENT_CHAR_LIMIT;
  const displayedContent = isLongContent && !showFullContent
    ? announcement.content.slice(0, CONTENT_CHAR_LIMIT).trimEnd() + "…"
    : announcement.content;
  const expiryStatus = getExpiryStatus(announcement.createdAt);

  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [editImagePreview, setEditImagePreview] = useState<string | null>(announcement.imageUrl || null);
  const [editRemoveImage, setEditRemoveImage] = useState(false);

  const editForm = useForm<AnnouncementFormValues>({
    resolver: zodResolver(announcementSchema),
    defaultValues: { title: announcement.title, content: announcement.content },
  });

  const commentForm = useForm<CommentFormValues>({
    resolver: zodResolver(commentSchema),
    defaultValues: { content: "" },
  });

  const { data: detailsData, isLoading: isLoadingDetails } = useQuery({
    queryKey: ["announcements", announcement.id],
    queryFn: async () => {
      const res = await api.get<{ announcement: Announcement }>(`/api/announcements/${announcement.id}`);
      return res.data.announcement;
    },
    enabled: isExpanded,
  });

  const handleCommentSubmit = (values: CommentFormValues) => {
    onComment(values.content);
    commentForm.reset();
  };

  const handleEditSubmit = async (values: AnnouncementFormValues) => {
    if (!onUpdate) return;
    setIsUpdating(true);
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("content", values.content);
    if (editImageFile) {
      formData.append("image", editImageFile);
    } else if (editRemoveImage) {
      formData.append("removeImage", "true");
    }
    try {
      await onUpdate(announcement.id, formData);
      setIsEditing(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const commentsCount = detailsData?.comments?.length || 0;

  return (
    <>
      <SpotlightCard className="overflow-hidden transition-all duration-300">
        <div
        className={cn("p-5 cursor-pointer hover:bg-white/[0.02] transition-colors", isExpanded && "bg-white/[0.02]")}
        onClick={onToggle}
      >
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-start gap-5 flex-1 w-full min-w-0">
            {announcement.imageUrl && (
              <div
                className="group relative w-full sm:w-64 shrink-0 rounded-xl overflow-hidden border border-white/10 bg-slate-950 aspect-video shadow-md cursor-pointer flex items-center justify-center"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxImage(announcement.imageUrl!);
                }}
              >
                <img src={announcement.imageUrl} alt="Announcement" className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-[1.02]" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <span className="px-3 py-1.5 rounded-lg bg-gradient-to-b from-brand-400 to-brand-600 text-white font-medium text-xs shadow-glow transition-all transform active:scale-95">
                    View Full Image
                  </span>
                </div>
              </div>
            )}
            <div className="flex items-start gap-4 flex-1 min-w-0">
              {!isEditing && <Avatar name={announcement.authorName} url={announcement.authorAvatar} size="md" />}
              <div className="flex-1 min-w-0">
                {isEditing ? (
                  <form onSubmit={editForm.handleSubmit(handleEditSubmit)} onClick={(e) => e.stopPropagation()} className="w-full space-y-4">
                    <input {...editForm.register("title")} className="input" placeholder="Title" />
                    <textarea {...editForm.register("content")} rows={4} className="textarea" placeholder="Content" />
                    <div className="flex items-center gap-4">
                      <label className="btn-secondary cursor-pointer shrink-0 py-1.5 text-xs">
                        <ImageIcon className="w-4 h-4" />
                        {editImageFile || (!editRemoveImage && announcement.imageUrl) ? "Change Image" : "Upload Image"}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setEditImageFile(file);
                            setEditImagePreview(URL.createObjectURL(file));
                            setEditRemoveImage(false);
                          }
                        }} />
                      </label>
                      {editImagePreview && !editRemoveImage && (
                        <div className="relative w-12 h-12 rounded-xl overflow-hidden border border-white/10 shrink-0">
                          <img src={editImagePreview} alt="Preview" className="w-full h-full object-cover" />
                          <button type="button" onClick={() => {
                            setEditImageFile(null);
                            setEditImagePreview(null);
                            setEditRemoveImage(true);
                          }} className="absolute top-0.5 right-0.5 p-0.5 bg-black/50 rounded-full text-white hover:bg-black/70">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => setIsEditing(false)} className="btn-secondary py-1.5 px-3 text-xs">Cancel</button>
                      <button type="submit" disabled={isUpdating} className="btn-primary py-1.5 px-3 text-xs">
                        {isUpdating ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="text-lg font-semibold text-white group-hover:text-brand-400 transition-colors">
                        {announcement.title}
                      </h3>
                      {canEdit && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
                            className="p-1.5 text-slate-400 hover:text-white transition-colors bg-white/5 rounded-lg hover:bg-white/10"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onDelete && window.confirm("Are you sure you want to delete this announcement?")) onDelete(announcement.id);
                            }}
                            className="p-1.5 text-slate-400 hover:text-critical-400 transition-colors bg-white/5 rounded-lg hover:bg-white/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-slate-400 flex-wrap">
                      <span className="font-medium text-slate-300">{announcement.authorName}</span>
                      <span>•</span>
                      <span>{format(new Date(announcement.createdAt), "MMM d, yyyy 'at' h:mm a")}</span>
                      <span>•</span>
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold",
                          expiryStatus.variant === "expired" && "bg-red-500/15 text-red-400",
                          expiryStatus.variant === "critical" && "bg-orange-500/15 text-orange-400 animate-pulse",
                          expiryStatus.variant === "warning" && "bg-amber-500/15 text-amber-400",
                          expiryStatus.variant === "ok" && "bg-emerald-500/15 text-emerald-400",
                        )}
                      >
                        {expiryStatus.variant === "expired" ? (
                          <AlertTriangle className="w-3 h-3" />
                        ) : (
                          <Clock className="w-3 h-3" />
                        )}
                        {expiryStatus.label}
                      </span>
                    </div>
                    <p className={cn(
                      "mt-3 text-slate-300 whitespace-pre-wrap text-sm leading-relaxed",
                      expiryStatus.variant === "expired" && "opacity-60"
                    )}>
                      {displayedContent}
                    </p>
                    {isLongContent && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowFullContent(!showFullContent);
                        }}
                        className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-brand-400 hover:text-brand-300 transition-colors"
                      >
                        {showFullContent ? (
                          <><ChevronUp className="w-3.5 h-3.5" /> See less</>
                        ) : (
                          <><ChevronDown className="w-3.5 h-3.5" /> See more</>
                        )}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 text-slate-400 shrink-0 mt-4 sm:mt-0">
            <div className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-white/5">
              <MessageSquare className="w-3.5 h-3.5" />
              {isExpanded ? (commentsCount > 0 ? commentsCount : "Reply") : "Click to view comments"}
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-white/5 bg-white/[0.02] p-5 animate-in slide-in-from-top-2 fade-in">
          {isLoadingDetails ? (
            <div className="flex justify-center py-4">
              <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {detailsData?.comments && detailsData.comments.length > 0 ? (
                <div className="space-y-4">
                  {detailsData.comments.map((comment) => (
                    <div key={comment.id} className="flex gap-3">
                      <Avatar name={comment.authorName} url={comment.authorAvatar} size="sm" />
                      <div className="flex-1 bg-white/5 rounded-2xl rounded-tl-sm px-4 py-3">
                        <div className="flex items-baseline justify-between mb-1">
                          <span className="font-medium text-sm text-white">{comment.authorName}</span>
                          <span className="text-xs text-slate-500">
                            {format(new Date(comment.createdAt), "MMM d, h:mm a")}
                          </span>
                        </div>
                        <p className="text-sm text-slate-300 whitespace-pre-wrap">{comment.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-2">No comments yet. Be the first to reply!</p>
              )}

              <form onSubmit={commentForm.handleSubmit(handleCommentSubmit)} className="flex items-start gap-3 mt-4">
                <div className="flex-1">
                  <input
                    {...commentForm.register("content")}
                    placeholder="Write a comment..."
                    className="input rounded-full"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isCommenting || !commentForm.formState.isDirty}
                  className="p-2.5 bg-gradient-to-b from-brand-400 to-brand-600 text-white rounded-full hover:brightness-110 active:brightness-95 transition-all disabled:opacity-50 shrink-0"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          )}
        </div>
      )}
    </SpotlightCard>

    {lightboxImage && (
      <div
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-slate-950/90 backdrop-blur-md p-4 transition-all duration-300"
        onClick={() => setLightboxImage(null)}
      >
        <div
          className="relative max-w-5xl w-full flex flex-col items-center gap-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-full flex justify-end">
            <button
              onClick={() => setLightboxImage(null)}
              className="btn-ghost text-white hover:text-brand-300 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="relative rounded-2xl overflow-hidden bg-slate-900 border border-white/10 shadow-2xl">
            <img
              src={lightboxImage}
              alt="Full view"
              className="max-h-[80vh] w-auto object-contain"
            />
          </div>
        </div>
      </div>
    )}
  </>
  );
}
